import { ArrowRight } from 'lucide-react'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'

import { PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import NextImage from '@/components/NextImage'
import { Button } from '@/components/ui/button'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface CourseItem {
  title: string
  subtitle: string
  description: string[] | Array<{ point: string }> | string
  image: string
  imageAlt: string
  curriculumUrl: string
}

/** CourseItem after normalization — description is always string[] */
interface NormalizedCourseItem extends Omit<CourseItem, 'description'> {
  description: string[]
}

/**
 * Normalize course items from Puck editor.
 * Puck array fields with children produce objects like {point: 'text'}
 * instead of plain strings. This normalizer handles both formats.
 */
function normalizeCourses(courses: CourseItem[]): NormalizedCourseItem[] {
  return courses.map(course => {
    let description: string[]
    if (Array.isArray(course.description)) {
      description = course.description.map(item =>
        typeof item === 'string' ? item : (item as { point: string }).point || ''
      )
    } else if (typeof course.description === 'string' && course.description) {
      description = [course.description]
    } else {
      description = []
    }
    return { ...course, description }
  })
}

interface CoursePathSectionProps {
  className?: string
  /** Section heading (supports HTML) */
  heading?: string
  /** Section subheading */
  subheading?: string
  /** Heading text color */
  headingColor?: string
  /** Subheading text color */
  subheadingColor?: string
  /** Description text color */
  descriptionColor?: string
  /** Background color */
  bgColor?: string
  /** Data source: 'built-in' or 'custom' */
  dataSource?: 'built-in' | 'custom'
  /** Override default courses (custom mode) */
  courses?: CourseItem[]
  /** Enable scrollspy navigation */
  enableScrollspy?: boolean
}

// Default courses data
const defaultCourses: CourseItem[] = [
  {
    title: 'DSAI',
    subtitle: 'Khóa <span class="text-primary">Data Science & AI</span> (DSAI)',
    description: [
      '<b>Khóa Data Science & AI (DSAI):</b> Học viên được rèn luyện tư kiến thức toán học nền tảng đến kỹ năng modeling (thiết kế, tối ưu model cho industry), kết hợp với analytics và domain knowledge.',
      '<b>Các module con:</b> Machine Learning, Deep Learning.',
      '<b>Prerequisite:</b> Không yêu cầu.',
      '<b>Thời gian học:</b> Dự kiến 6 tháng (bao gồm cả thời gian làm đồ án).',
    ],
    image: '/images/home/v6/dsaa.png',
    imageAlt: 'Learner coding machine learning model on desktop screen',
    curriculumUrl:
      'https://docs.google.com/spreadsheets/d/1fwiYwyRgVddt57K0GROvjdT3a6ZsTbKbid5Zy3IpNkQ/edit?gid=2068386081#gid=2068386081',
  },
  {
    title: 'EDAI',
    subtitle: 'Khóa <span class="text-primary">Engineering for Data & AI</span> (EDAI)',
    description: [
      '<b>Khóa Engineering for Data & AI (EDAI):</b> Học viên được học các kiến thức từ cơ bản đến nâng cao về software engineering để phát triển hệ thống Data & AI một cách có hiệu quả.',
      '<b>Các module con:</b> Data Engineering, Machine Learning Engineering.',
      '<b>Prerequisite:</b> Không yêu cầu.',
      '<b>Thời gian học:</b> Dự kiến 6 tháng (bao gồm cả thời gian làm đồ án).',
    ],
    image: '/images/home/v6/aide.png',
    imageAlt: 'Team collaborating on AI and data engineering project setup',
    curriculumUrl:
      'https://docs.google.com/spreadsheets/d/1YMXPRRRKCsbGY4Z8lSGgDEccF5NUmo_k/edit?gid=221663282#gid=221663282',
  },
  {
    title: 'BAIW',
    subtitle: 'Khóa <span class="text-primary">Build AI-Integrated Website</span> (BAIW)',
    description: [
      '<b>Khóa Build AI-Integrated Website (BAIW):</b> Giúp học viên nắm vững nền tảng Web Development bằng cách tự tay xây dựng một hệ thống e-learning hoàn chỉnh, tích hợp Recommendation Engine và Smart Chatbot.',
      '<b>Nội dung chính:</b> Frontend (HTML/CSS/JS, React, Routing & State), Backend (Node.js, Express, MongoDB), Authentication (JWT), Recommendation Engine, Deployment, và tích hợp LLM Chatbot.',
      '<b>Prerequisite:</b> Không yêu cầu.',
      '<b>Thời gian học:</b> Dự kiến 3 tháng (bao gồm cả thời gian làm đồ án).',
    ],
    image: '/images/home/v6/baiw.jpg',
    imageAlt: 'Student building AI-integrated e-learning website with chatbot and recommendation engine',
    curriculumUrl:
      'https://docs.google.com/spreadsheets/d/14Y3a6GkVvr4iptyiEOPVkEH8v-Y_Xff0/edit?gid=908181129#gid=908181129',
  },
]

export const coursePathSectionSchema: SectionElementSchema = {
  componentType: 'CoursePathSection',
  label: 'Course Path Section',
  category: 'content',
  description: 'Course path section with scrollspy navigation and course details',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main heading (supports HTML)',
      default: 'Lộ trình Data & AI <span class="text-primary">từ con số 0</span>',
    },
    {
      key: 'subheading',
      type: 'textarea',
      label: 'Subheading',
      default:
        'Nội dung được thiết kế dựa trên kinh nghiệm thực tế + tham chiếu chương trình MSc AI & Data Engineering @ UCL',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'subheadingColor',
      type: 'color',
      label: 'Subheading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'descriptionColor',
      type: 'color',
      label: 'Description Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      default: 'transparent',
    },
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      options: [
        { label: 'Built-in (Default)', value: 'built-in' },
        { label: 'Custom Items', value: 'custom' },
      ],
      default: 'built-in',
    },
    {
      key: 'courses',
      type: 'array',
      label: 'Courses',
      helpText: 'Add, edit, or remove course items (only when Data Source is "Custom")',
      default: defaultCourses,
      children: [
        { key: 'title', type: 'text', label: 'Course Code', required: true, default: defaultCourses[0].title },
        {
          key: 'subtitle',
          type: 'textarea',
          label: 'Subtitle (HTML)',
          required: true,
          default: defaultCourses[0].subtitle,
        },
        {
          key: 'description',
          type: 'array',
          label: 'Description Points',
          helpText: 'Array of HTML description points',
          default: (defaultCourses[0].description as string[]).map((point: string) => ({ point })),
          children: [
            { key: 'point', type: 'textarea', label: 'Point (HTML)', default: defaultCourses[0].description[0] },
          ],
        },
        {
          key: 'image',
          type: 'image',
          label: 'Course Image',
          required: true,
          default: defaultCourses[0].image,
        },
        { key: 'imageAlt', type: 'text', label: 'Image Alt Text', default: defaultCourses[0].imageAlt },
        { key: 'curriculumUrl', type: 'text', label: 'Curriculum URL', default: defaultCourses[0].curriculumUrl },
      ],
    },
    {
      key: 'enableScrollspy',
      type: 'boolean',
      label: 'Enable Scroll Navigation',
      helpText: 'Show scrollspy-based tab navigation',
      default: true,
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

sectionSchemaRegistry.register(coursePathSectionSchema)

function CoursePathSection({
  className,
  heading = 'Lộ trình Data & AI <span class="text-primary">từ con số 0</span>',
  subheading = 'Nội dung được thiết kế dựa trên kinh nghiệm thực tế + tham chiếu chương trình MSc AI & Data Engineering @ UCL',
  headingColor,
  subheadingColor,
  descriptionColor,
  bgColor = 'transparent',
  dataSource = 'built-in',
  courses: customCourses,
  enableScrollspy = true,
}: CoursePathSectionProps) {
  const [activeTab, setActiveTab] = React.useState(0)
  const sectionRefs = React.useRef<(HTMLDivElement | null)[]>([])

  // Determine which courses to display based on dataSource
  const rawCourses = dataSource === 'custom' && customCourses?.length ? customCourses : defaultCourses
  const courses = normalizeCourses(rawCourses)

  const scrollToSection = (index: number) => {
    const element = sectionRefs.current[index]
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  React.useEffect(() => {
    if (!enableScrollspy) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = sectionRefs.current.findIndex(ref => ref === entry.target)
            if (index !== -1) {
              setActiveTab(index)
            }
          }
        })
      },
      {
        threshold: 0,
        rootMargin: '-40% 0px -40% 0px',
      }
    )

    sectionRefs.current.forEach(ref => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [enableScrollspy])

  return (
    <Shell
      className={cn('px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12 lg:py-16', className)}
      style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : undefined }}
    >
      <PageHeaderHeading
        as="h2"
        size="lg"
        className="tracking-normal text-2xl sm:text-3xl md:text-4xl lg:text-5xl/tight md:leading-tight text-center mb-4 lg:mb-0"
        style={headingColor ? { color: headingColor } : undefined}
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(heading) }}
      />
      <p
        className="text-center sm:text-left sm:text-lg mb-4 lg:mb-12 mx-auto text-gray-600"
        style={subheadingColor ? { color: subheadingColor } : undefined}
      >
        {subheading}
      </p>

      <div>
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
          {enableScrollspy && (
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-white/90 rounded-2xl px-6 py-4">
                  <div className="flex flex-col space-y-2">
                    {courses.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection(index)}
                        className={cn(
                          'px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-left',
                          activeTab === index
                            ? 'bg-primary text-white shadow-md'
                            : 'text-gray-600 hover:text-primary hover:bg-gray-100'
                        )}
                      >
                        {section.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {enableScrollspy && (
            <div className="lg:hidden sticky top-20 z-10 mb-6 px-4">
              <div className="flex justify-center w-full">
                <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl px-4 py-3 shadow-lg w-full">
                  <div className="flex justify-between space-x-1">
                    {courses.map((section, index) => (
                      <button
                        key={index}
                        onClick={() => scrollToSection(index)}
                        className={cn(
                          'flex-1 px-3 py-2 text-xs font-medium transition-all duration-300 rounded-xl',
                          activeTab === index
                            ? 'bg-primary text-white shadow-md'
                            : 'text-gray-600 hover:text-primary hover:bg-gray-100'
                        )}
                      >
                        {section.title}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 space-y-8 lg:space-y-12">
            {courses.map((feature, index) => (
              <div
                key={index}
                ref={el => {
                  sectionRefs.current[index] = el
                }}
                className="space-y-6 lg:space-y-8 scroll-mt-24"
              >
                <div className="relative rounded-2xl overflow-hidden transition-all duration-300">
                  <div className="flex flex-col lg:flex-row-reverse gap-6 lg:gap-12">
                    <div className="space-y-4 lg:space-y-6 py-6 pr-4">
                      <div className="flex items-start justify-between gap-4">
                        <h3
                          className="text-xl lg:text-3xl font-bold text-gray-900 leading-tight"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(feature.subtitle) }}
                        />
                      </div>

                      <div className="space-y-3 lg:space-y-4">
                        {feature.description.map((desc, descIndex) => (
                          <div key={descIndex} className="flex items-start space-x-3 lg:space-x-4">
                            <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2 lg:mt-3" />
                            <p
                              className="text-sm lg:text-lg text-gray-700 leading-relaxed"
                              style={descriptionColor ? { color: descriptionColor } : undefined}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(desc) }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="w-full flex justify-center sm:justify-start">
                        <Button
                          variant="default"
                          size="lg"
                          onClick={() => window.open(feature.curriculumUrl, '_blank')}
                        >
                          Xem chi tiết <ArrowRight />
                        </Button>
                      </div>
                    </div>

                    <div className="relative mx-auto w-full sm:min-w-[400px] sm:max-w-[400px]">
                      <NextImage
                        src={feature.image}
                        alt={feature.imageAlt}
                        width={400}
                        height={400}
                        quality={100}
                        className="w-full h-full"
                        sizes="(min-width: 1024px) 100vw, (min-width: 768px) 60vw, 100vw"
                        classNames={{
                          image: 'object-cover object-center w-full h-full rounded-2xl',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  )
}

export default CoursePathSection
